import { useRef, useState } from 'react'

export type RecordingState = 'idle' | 'recording' | 'stopped'

export interface UseVoiceRecorderResult {
  state: RecordingState
  audioBlob: Blob | null
  audioUrl: string | null
  durationMs: number
  start: () => Promise<void>
  stop: () => void
  reset: () => void
  error: string | null
}

function getSupportedMimeType(): string {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? 'audio/webm'
}

export function useVoiceRecorder(): UseVoiceRecorderResult {
  const [state, setState] = useState<RecordingState>('idle')
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [durationMs, setDurationMs] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const startTimeRef = useRef<number>(0)

  async function start() {
    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = getSupportedMimeType()
      const mr = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mr
      chunksRef.current = []
      startTimeRef.current = Date.now()

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        const url = URL.createObjectURL(blob)
        setAudioBlob(blob)
        setAudioUrl(url)
        setDurationMs(Date.now() - startTimeRef.current)
        stream.getTracks().forEach((t) => t.stop())
      }

      mr.start(250)
      setState('recording')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone access denied')
    }
  }

  function stop() {
    mediaRecorderRef.current?.stop()
    setState('stopped')
  }

  function reset() {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioBlob(null)
    setAudioUrl(null)
    setDurationMs(0)
    setError(null)
    setState('idle')
    chunksRef.current = []
  }

  return { state, audioBlob, audioUrl, durationMs, start, stop, reset, error }
}
